import {
  INITIAL_HOUSING_SA_STATE,
  type SAState,
  SearchReason,
} from "~/features/selectionAssistant/housing/redux/types";
import {
  BEGIN_SEARCH,
  HSA_SEARCH_SUCCESS,
  type HSAActions,
  LOAD_INITIAL_DATA,
  SEARCH_FAIL,
} from "~/features/selectionAssistant/housing/redux/actions";
import { toMapFn } from "~/common/utils/reducers";
import { type FacetData } from "~/utils/models";
import { createFilterDefinitions } from "~/features/selectionAssistant/housing/redux/createFilterDefinitions";

export const HOUSING_SA_REDUCER = (state = INITIAL_HOUSING_SA_STATE, action: HSAActions) => {
  let hasChanged = false;

  const newState = BASE_HOUSING_REDUCER(state, action);
  if (newState !== state) {
    hasChanged = true;
  }

  const configState = state.configuration;
  const newConfigState = CONFIGURATION_REDUCER(configState, action);
  newState.configuration = newConfigState;
  if (newConfigState !== configState) {
    hasChanged = true;
  }

  const resultsState = state.searchResults;
  const newResultsState = RESULTS_REDUCER(resultsState, action);
  newState.searchResults = newResultsState;
  if (newResultsState !== resultsState) {
    hasChanged = true;
  }

  return hasChanged ? newState : state;
};

const BASE_HOUSING_REDUCER = (state: SAState, action: HSAActions) => {
  switch (action.type) {
    case LOAD_INITIAL_DATA: {
      return {
        ...state,
        initialised: true,
        inFlight: false,
      };
    }
    case BEGIN_SEARCH: {
      return {
        ...state,
        lastSearchReason: action.payload,
        inFlight: true,
        error: false,
      };
    }
    case HSA_SEARCH_SUCCESS: {
      return {
        ...state,
        inFlight: false,
        error: false,
      };
    }
    case SEARCH_FAIL: {
      return {
        ...state,
        inFlight: false,
        error: true,
      };
    }
    default: {
      return state;
    }
  }
};

const CONFIGURATION_REDUCER = (state: SAState["configuration"], action: HSAActions): SAState["configuration"] => {
  switch (action.type) {
    case LOAD_INITIAL_DATA: {
      const { metricColumnConfig, imperialColumnConfig, clusterGroups, searchResults } = action.payload;

      const filterConfig = createFilterDefinitions(
        clusterGroups,
        toMapFn(searchResults.facets, (facet) => facet.code),
      );

      return {
        ...state,
        metricColumnConfig,
        imperialColumnConfig,
        filterConfig,
      };
    }
    default: {
      return state;
    }
  }
};
const RESULTS_REDUCER = (state: SAState["searchResults"], action: HSAActions) => {
  switch (action.type) {
    case LOAD_INITIAL_DATA:
    case HSA_SEARCH_SUCCESS: {
      const { searchResults, searchReason } = action.payload;

      let facetsByCode;
      if (!Object.keys(state.facetsByCode).length) {
        facetsByCode = toMapFn(searchResults.facets, (facet) => facet.code);
      } else {
        facetsByCode = updateFacetsWithCount(
          state.facetsByCode,
          toMapFn(searchResults.facets, (facet) => facet.code),
        );
      }

      let results;
      let pageAmount;
      let pageSize;

      if (searchReason === SearchReason.FILTER) {
        results = searchResults.results;
        pageAmount = searchResults.pagination.numberOfPages;
        pageSize = searchResults.pagination.pageSize;
      } else if (searchReason === SearchReason.PAGINATION) {
        results = state.results.concat(searchResults.results);
        pageAmount = searchResults.pagination.numberOfPages;
        pageSize = searchResults.pagination.pageSize;
      } else {
        // searchReason === SearchReason.SORTING
        results = searchResults.results;
        pageAmount = state.pageAmount;
        pageSize = state.pageSize;
      }

      return {
        ...state,
        results,
        totalResults: searchResults.pagination.totalNumberOfResults,
        pageAmount,
        pageSize,
        facetsByCode,
      };
    }
    default: {
      return state;
    }
  }
};

const SLICES = {
  configuration: CONFIGURATION_REDUCER,
  searchResults: RESULTS_REDUCER,
} as const;

function updateFacetsWithCount(a: Dict<FacetData>, b: Dict<FacetData>): Dict<FacetData> {
  const result = { ...a };

  Object.values(a).forEach((facet) => {
    facet.values.forEach((value) => {
      value.count = b[facet.code]?.values.find((v) => v.code === value.code)?.count ?? 0;
    });
  });

  return result;
}
